import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MapContainer, TileLayer, CircleMarker, Tooltip, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import api from "../lib/api";
import GlobalFilters from "../components/GlobalFilters";
import { useLang } from "../lib/i18n";
import { PageHeader, pct, num } from "../lib/ui";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import provincesGeo from "../data/drc_provinces.json";
import { cacheGet, cacheSet } from "../lib/offline";
import { WifiOff } from "lucide-react";
import { TilePrefetch } from "../components/TilePrefetch";
import { useAuth } from "../context/AuthContext";

const INDICATORS = {
  entry_completeness: { label: "Complétude de saisie (%)", fmt: pct },
  reports: { label: "Rapports reçus", fmt: num },
  completeness: { label: "Complétude (%)", fmt: pct },
  alerts_received: { label: "Alertes reçues", fmt: num },
  suspected_cases: { label: "Cas suspects", fmt: num },
};
const COMP_KEYS = ["completeness", "entry_completeness"];

function markerColor(key, v) {
  if (v == null) return "#94A3B8";
  if (COMP_KEYS.includes(key)) return v >= 80 ? "#059669" : v >= 50 ? "#D97706" : "#DC2626";
  const t = key === "reports" ? [10, 25] : key === "alerts_received" ? [60, 150] : [30, 80];
  return v >= t[1] ? "#DC2626" : v >= t[0] ? "#D97706" : "#0284C7";
}
function radiusFor(v, max) { return !v || !max ? 7 : 7 + (v / max) * 20; }

function choroColor(ratio, isCompleteness, v) {
  if (isCompleteness) return v >= 80 ? "#047857" : v >= 50 ? "#D97706" : v == null ? "#CBD5E1" : "#DC2626";
  if (ratio == null) return "#CBD5E1";
  return ratio > 0.66 ? "#991B1B" : ratio > 0.33 ? "#C2410C" : "#0284C7";
}

export default function MapView() {
  const [filters, setFilters] = useState({});
  const { t } = useLang();
  const { user } = useAuth();
  const [indicator, setIndicator] = useState("alerts_received");
  const cacheKey = `map:${JSON.stringify(filters)}`;
  const [fromCache, setFromCache] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["map", filters],
    retry: false,
    queryFn: async () => {
      try {
        const res = (await api.get("/map-data", { params: filters })).data;
        await cacheSet(cacheKey, res);
        setFromCache(null);
        return res;
      } catch (err) {
        const c = await cacheGet(cacheKey);
        if (c?.value) { setFromCache(c.at); return c.value; }
        throw err;
      }
    },
  });
  const zones = data || [];
  const max = Math.max(1, ...zones.map((d) => d[indicator] || 0));

  // aggregate per province for the choropleth
  const provAgg = useMemo(() => {
    const agg = {};
    zones.forEach((z) => {
      const p = agg[z.province] || { reports: 0, alerts_received: 0, suspected_cases: 0, _c: [], };
      p.reports += z.reports || 0;
      p.alerts_received += z.alerts_received || 0;
      p.suspected_cases += z.suspected_cases || 0;
      if (z.completeness != null) p._c.push(z.completeness);
      agg[z.province] = p;
    });
    Object.values(agg).forEach((p) => {
      p.completeness = p._c.length ? Math.round(p._c.reduce((a, b) => a + b, 0) / p._c.length) : null;
    });
    return agg;
  }, [zones]);
  const provMax = Math.max(1, ...Object.values(provAgg).map((p) => p[indicator] || 0));
  const isComp = COMP_KEYS.includes(indicator);

  const style = (feature) => {
    const p = provAgg[feature.properties.name];
    const v = p ? p[indicator] : null;
    const ratio = p ? (p[indicator] || 0) / provMax : null;
    return { fillColor: choroColor(ratio, isComp, v), weight: 1.5, color: "#ffffff", fillOpacity: 0.45 };
  };
  const onEach = (feature, layer) => {
    const name = feature.properties.name;
    const p = provAgg[name];
    layer.bindTooltip(
      `<b>${name}</b><br/>${t("Rapports")}: ${p?.reports ?? 0}<br/>${t("Complétude")}: ${p?.completeness == null ? "N/A" : p.completeness + "%"}<br/>${t("Alertes")}: ${p?.alerts_received ?? 0} · ${t("Cas")}: ${p?.suspected_cases ?? 0}`,
      { sticky: true }
    );
  };

  return (
    <div data-testid="map-page">
      <PageHeader title={t("Carte de Surveillance · RDC")} subtitle={t("Choroplèthe provincial + volume par Zone de Santé")}>
        <div className="min-w-[220px]">
          <Label className="text-xs text-slate-500">{t("Indicateur affiché")}</Label>
          <Select value={indicator} onValueChange={setIndicator}>
            <SelectTrigger data-testid="map-indicator"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(INDICATORS).map(([k, v]) => <SelectItem key={k} value={k}>{t(v.label)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </PageHeader>
      <GlobalFilters value={filters} onChange={setFilters} />
      <TilePrefetch
        zones={zones.filter((z) => (user?.health_zone ? z.health_zone === user.health_zone : user?.province ? z.province === user.province : true))}
        label={user?.health_zone || user?.province || ""} />
      {fromCache && (
        <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="map-offline-banner">
          <WifiOff className="h-3.5 w-3.5" />
          {t("Hors connexion : carte affichée avec les dernières données chargées le")} {new Date(fromCache).toLocaleString("fr-FR")}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden" style={{ height: 560 }}>
        {isLoading ? <div className="h-full flex items-center justify-center text-slate-400">Chargement…</div> : (
          <MapContainer center={[-1.8, 29.2]} zoom={7} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <GeoJSON key={indicator + zones.length} data={provincesGeo} style={style} onEachFeature={onEach} />
            {zones.map((z) => (
              <CircleMarker key={z.health_zone} center={[z.lat, z.lng]} radius={radiusFor(z[indicator], max)}
                pathOptions={{ color: "#0f172a", fillColor: markerColor(indicator, z[indicator]), fillOpacity: 0.85, weight: 1 }}>
                <Tooltip>
                  <div className="text-xs">
                    <p className="font-semibold">{z.health_zone} <span className="text-slate-400">({z.province})</span></p>
                    <p>{t("Rapports")}: {z.reports} · {t("Complétude")}: {pct(z.completeness)}</p>
                    <p className="font-semibold text-[#F05A28]">
                      {t("Saisie quotidienne")}: {z.entry_completeness == null ? t("aucun agent affecté") : pct(z.entry_completeness)}
                    </p>
                    {z.entry_agents > 0 && (
                      <p>{z.entries_done}/{z.entry_agents * z.entry_days} {t("saisies")} · {z.entry_agents} {t("agent(s)")} · {t("dernière")}: {z.entry_last_date || t("jamais")}</p>
                    )}
                    <p>{t("Alertes")}: {z.alerts_received} · {t("Cas suspects")}: {z.suspected_cases}</p>
                  </div>
                </Tooltip>
              </CircleMarker>
            ))}
          </MapContainer>
        )}
      </div>

      <div className="flex flex-wrap gap-4 mt-4 text-xs text-slate-500">
        <span className="font-semibold">{t("Légende")} ({t(INDICATORS[indicator].label)}):</span>
        <span>{t("Les provinces sont colorées (choroplèthe) et les cercles indiquent le volume par zone de santé.")}</span>
        {isComp && (
          <span className="flex items-center gap-3" data-testid="map-completeness-legend">
            <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#059669" }} /> ≥ 80%</span>
            <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#D97706" }} /> 50–79%</span>
            <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#DC2626" }} /> &lt; 50%</span>
            <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#94A3B8" }} /> {t("sans agent affecté")}</span>
          </span>
        )}
      </div>
    </div>
  );
}
